/**
 * MSAL Configuration for Entra ID Authentication
 *
 * Environment Variables (set in .env):
 * - VITE_ENTRA_CLIENT_ID: Application (client) ID from Azure Portal
 * - VITE_ENTRA_TENANT_ID: Directory (tenant) ID from Azure Portal
 *
 * Azure Portal Setup:
 * 1. Go to Entra ID > App registrations > New registration
 * 2. Name: "CherryPulse"
 * 3. Supported account types: "Single tenant" (nur euer Tenant)
 * 4. Redirect URI: "Single-page application (SPA)" with:
 *    - https://www.cherrypulse.de (production)
 *    - https://app-cherrypulse.azurewebsites.net (App Service default host)
 *    - http://localhost:5173 (development)
 * 5. Copy Application (client) ID and Directory (tenant) ID
 */

import { Configuration, LogLevel, PopupRequest } from '@azure/msal-browser';

// Validate required environment variables
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string;
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string;

if (!clientId || !tenantId) {
  console.warn(
    'MSAL Config: VITE_ENTRA_CLIENT_ID and VITE_ENTRA_TENANT_ID must be set in .env'
  );
}

/**
 * MSAL Configuration
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: clientId || 'missing-client-id',
    authority: `https://login.microsoftonline.com/${tenantId || 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
          case LogLevel.Info:
            // Only log in development
            if (import.meta.env.DEV) {
              console.info('[MSAL]', message);
            }
            break;
          case LogLevel.Verbose:
            // Only log in development with DEBUG flag
            if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_MSAL) {
              console.debug('[MSAL]', message);
            }
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
    },
  },
};

/**
 * Scopes for login
 * Includes User.Read for Microsoft Graph API to fetch profile photos
 */
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Scopes for Microsoft Graph API calls
 */
export const graphRequest = {
  scopes: ['User.Read'],
};

/**
 * Export tenant ID for backend validation
 */
export const entraConfig = {
  clientId,
  tenantId,
  issuer: `https://login.microsoftonline.com/${tenantId}/v2.0`,
};
